// The WGSL twins of the four arrival veils (`../../layers/spawn.ts`).
//
// A veil is a screen fragment pass: the deep sea over the map, lifting. The
// GLSL body states no figure of its own and neither does the twin — every
// colour, radius, band and fade reaches the shader as a uniform, and
// `spawn-shaders.test.ts` holds BOTH halves to the four structural numbers
// `0.0`, `1.0`, `0.5` and `2.0`.
//
// Every edge is a `smoothstep`, because the client ships without MSAA
// (R491). The one thing the language forces is the ternary: `select` takes
// its FALSE arm first.


/**
 * The deep sea every veil is painted in, and the fade they all share.
 *
 * The reference bakes a 64 × 512 gradient canvas and stretches it over the
 * viewport; the same three stops down `vUV.y` cost one mix here and no
 * texture at all.
 */
const DEEP_GRADIENT_WGSL = `fn deep(t: f32) -> vec3f {
  return select(mix(U.uMidColor, U.uLowColor, (t - 0.5) * 2.0),
                mix(U.uTopColor, U.uMidColor, t * 2.0),
                t < 0.5);
}
`;

/** The plain fade: the deep sea over the map, lifting. */
export const VEIL_FADE_WGSL = `@fragment
fn fs(in: VsOut) -> @location(0) vec4f {
  let a = U.uVeilAlpha;
  return vec4f(deep(in.vUV.y) * a, a);
}
`;

/**
 * Sonar Bloom's hole. One `smoothstep` on the distance from the home hold
 * keeps the reference's ramp: clear to `uHoleInner` of the radius, and the
 * full veil at the radius. A radius of 0 opens no hole.
 */
export const VEIL_BLOOM_WGSL = `@fragment
fn fs(in: VsOut) -> @location(0) vec4f {
  let p = in.vUV * U.uScreen;
  let hole = smoothstep(U.uHoleInner * U.uRadius, U.uRadius, length(p - U.uHome));
  let a = U.uVeilAlpha * select(1.0, hole, U.uRadius > 0.0);
  return vec4f(deep(in.vUV.y) * a, a);
}
`;

/**
 * Caustic Sweep's front. The band is a `smoothstep` on the distance along
 * the axis from the front, so the ramp needs no sprite and no texture.
 */
export const VEIL_SWEEP_WGSL = `@fragment
fn fs(in: VsOut) -> @location(0) vec4f {
  let p = in.vUV * U.uScreen;
  let s = dot(p, U.uAxis) - U.uFront;
  let a = U.uVeilAlpha * smoothstep(-U.uBand, U.uBand, s);
  return vec4f(deep(in.vUV.y) * a, a);
}
`;

/**
 * Rise from the Deep's pressure floor: the dark ground at `uVeilAlpha` and
 * the three-stop haze over it at `uHaze`, which lets go last.
 */
export const VEIL_RISE_WGSL = `// The haze colour and its alpha at the screen fraction t, top to bottom.
fn haze(t: f32) -> vec4f {
  let c = select(mix(U.uHazeMid, U.uHazeLow, (t - 0.5) * 2.0),
                 mix(U.uHazeTop, U.uHazeMid, t * 2.0),
                 t < 0.5);
  let a = select(mix(U.uHazeA.y, U.uHazeA.z, (t - 0.5) * 2.0),
                 mix(U.uHazeA.x, U.uHazeA.y, t * 2.0),
                 t < 0.5);
  return vec4f(c, a * U.uHaze);
}

@fragment
fn fs(in: VsOut) -> @location(0) vec4f {
  let a = U.uVeilAlpha;
  let g = vec4f(deep(in.vUV.y) * a, a);
  let z = haze(in.vUV.y);
  // The haze over the ground, both premultiplied.
  return vec4f(z.rgb * z.a + g.rgb * (1.0 - z.a), z.a + g.a * (1.0 - z.a));
}
`;

/**
 * One veil's BODY: the deep gradient every veil shares and the veil's own
 * fragment half.
 *
 * The header and the screen vertex entry are assembled by `worldProgram`
 * (`../../gfx/worldquad.ts`), which is the one assembly rule every pass of
 * the engine takes.
 */
export function veilBodyWgsl(body: string): string {
  return `${DEEP_GRADIENT_WGSL}\n${body}`;
}
